import React, { useState, useEffect } from "react";
import {
  Feed,
  MapsHomeWork,
  ArrowBack,
  Done,
  Edit,
  Shortcut,
} from "@mui/icons-material";
import {
  Typography,
  Box,
  Button,
  Alert,
  Snackbar,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TextField,
  InputAdornment,
  Chip,
  Divider,
  Avatar,
} from "@mui/material";
import axios from "axios";
import apiUrl from "../api";
import io from "socket.io-client";
import { AddTicketForm, ViewProperty, TicketTable } from "../../components";
import "./ViewPoolActionForm.css";

function formatDate(date) {
  if (!date) return "";
  date = new Date(date);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

const ViewPoolActionForm = ({
  poolAction,
  propertyName,
  poolOptions,
  onClose,
  onComplete,
  onUpdate,
}) => {
  const [activeView, setActiveView] = useState("main"); // main, ticket, property, edit, relatedTickets
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editedOptions, setEditedOptions] = useState(
    poolAction.pool_options.map(option => ({...option}))
  );
  // Add state to track the current pool action data that reflects any updates
  const [currentPoolAction, setCurrentPoolAction] = useState(poolAction);

  const user = JSON.parse(localStorage.getItem("user"));

  // Update editedOptions when poolAction changes (from parent or socket)
  useEffect(() => {
    setEditedOptions(poolAction.pool_options.map(option => ({...option})));
    setCurrentPoolAction(poolAction);
  }, [poolAction]);

  const handlePriceChange = (optionId, newPrice) => {
    setEditedOptions(currentOptions => 
      currentOptions.map(option => 
        option._id === optionId 
          ? {...option, price: Number(newPrice)} 
          : option
      )
    );
  };

  const handleValueChange = (optionId, newValue) => {
    // Convert comma decimal separators to dots and handle numeric values
    const processedValue = newValue.toString().replace(',', '.');
    
    setEditedOptions(currentOptions => 
      currentOptions.map(option => 
        option._id === optionId 
          ? {...option, value: processedValue} 
          : option
      )
    );
  };

  const handleSaveChanges = async () => {
    setIsSubmitting(true);
    try {
      const updatedPoolAction = {
        ...currentPoolAction,
        pool_options: editedOptions
      };
      
      const response = await axios.put(
        `${apiUrl}/pool-action/${poolAction._id}`,
        updatedPoolAction,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      
      // Update local state with response data
      setCurrentPoolAction(response.data);
      
      setSuccessMessage("Changes saved successfully!");
      setShowSuccessAlert(true);
      if (onUpdate) onUpdate(response.data);
      
      setTimeout(() => {
        setActiveView("main");
      }, 2000);
    } catch (error) {
      console.error("Error saving changes:", error);
      setSuccessMessage("Error saving changes");
      setShowSuccessAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    try {
      await axios.patch(
        `${apiUrl}/pool-action/${poolAction._id}/done`,
        { time_done: new Date() },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      
      setSuccessMessage("Pool action completed successfully!");
      setShowSuccessAlert(true);
      if (onComplete) onComplete(poolAction._id);
      
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (error) {
      console.error("Error completing pool action:", error);
      setSuccessMessage("Error completing pool action");
      setShowSuccessAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTotalPrice = () => {
    const total = editedOptions.reduce((sum, option) => {
      // Convert comma decimal separators to dots for parsing
      const processedValue = option.value ? option.value.toString().replace(',', '.') : option.value;
      // Only add to total if value is numeric
      const value = Number(processedValue);
      return sum + (Number(option.price) * (isNaN(value) ? 0 : value));
    }, 0);
    
    // Format to exactly 2 decimal places
    return total.toFixed(2);
  };

  const handleChargeChange = async (newCharge) => {
    setIsSubmitting(true);
    try {
      const updatedPoolAction = {
        ...currentPoolAction,
        charge: Number(newCharge)
      };
      
      const response = await axios.put(
        `${apiUrl}/pool-action/${poolAction._id}`,
        updatedPoolAction,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      
      setCurrentPoolAction(response.data);
      setSuccessMessage("Charge updated successfully!");
      setShowSuccessAlert(true);
      if (onUpdate) onUpdate(response.data);
    } catch (error) {
      console.error("Error updating charge:", error);
      setSuccessMessage("Error updating charge");
      setShowSuccessAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getOptionTitle = (optionId) => {
    const option = poolOptions.find(opt => opt._id === optionId);
    return option ? option.title : 'Unknown Option';
  };

  const handleCloseAlert = () => {
    setShowSuccessAlert(false);
  };

  // Get background color for the status chip
  const getStatusBgColor = () => poolAction.done ? '#e8f5e9' : '#e3f2fd';
  const getStatusColor = () => poolAction.done ? 'green' : 'blue';
  const getStatusText = () => poolAction.done ? 'Completed' : 'In Progress';

  // Add socket state
  const [socket, setSocket] = useState(null);
  
  // Connect to socket when component mounts
  useEffect(() => {
    const newSocket = io(apiUrl);
    setSocket(newSocket);
    
    // Clean up on unmount
    return () => {
      newSocket.disconnect();
    };
  }, []);
  
  // Listen for socket events related to this specific pool action
  useEffect(() => {
    if (!socket) return;
    
    socket.on('poolAction', (data) => {
      // Only handle events for this specific pool action
      if (data.action === 'update' && data.poolAction && data.poolAction._id === poolAction._id) {
        // Update both our local states with the latest data
        setCurrentPoolAction(data.poolAction);
        setEditedOptions(data.poolAction.pool_options.map(option => ({...option})));
        
        // Show success message if action was completed
        if (data.poolAction.done && !currentPoolAction.done) {
          setSuccessMessage("Pool action was completed by another user");
          setShowSuccessAlert(true);
          
          // Notify parent about the update
          if (onUpdate) onUpdate(data.poolAction);
        }
      } else if (data.action === 'delete' && data.poolActionId === poolAction._id) {
        setSuccessMessage("This pool action has been deleted");
        setShowSuccessAlert(true);
        
        // Close the form after a brief delay
        setTimeout(() => {
          onClose();
        }, 2000);
      }
    });
    
    return () => {
      socket.off('poolAction');
    };
  }, [socket, poolAction._id, currentPoolAction]);

  return (
    <Box className="view-pool-action-form" sx={{ maxWidth: 600, width: '100%' }}>
      {/* Success Alert */}
      <Snackbar 
        open={showSuccessAlert} 
        autoHideDuration={2000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseAlert} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Back Button - Only show when not on main view */}
      {activeView !== 'main' && (
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          sx={{ mb: 2 }}
          onClick={() => setActiveView('main')}
        >
          Return
        </Button>
      )}

      {/* Main View */}
      {activeView === 'main' && (
        <>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', textAlign: 'center' }}>
            {propertyName}
          </Typography>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">Date</Typography>
              <Typography>{formatDate(poolAction.date)}</Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">Time</Typography>
              <Typography>{poolAction.time || "-"}</Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle1" fontWeight="bold">Status</Typography>
              <Chip 
                label={getStatusText()}
                sx={{ 
                  bgcolor: getStatusBgColor(),
                  color: getStatusColor(),
                  fontWeight: 'medium',
                  '& .MuiChip-label': { px: 1 }
                }}
              />
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />
          
          <Typography variant="h6" gutterBottom>Pool Options</Typography>
          
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Option</TableCell>
                  <TableCell align="right">Price</TableCell>
                  <TableCell align="right">Value</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentPoolAction.pool_options.map((option, idx) => {
                  // Convert comma decimal separators to dots for parsing
                  const processedValue = option.value ? option.value.toString().replace(',', '.') : option.value;
                  // Check if value is numeric for subtotal calculation
                  const value = Number(processedValue);
                  const isNumeric = !isNaN(value);
                  const subtotal = isNumeric ? option.price * value : "-";
                  
                  return (
                    <TableRow key={idx}>
                      <TableCell>{getOptionTitle(option._id)}</TableCell>
                      <TableCell align="right">{option.price} €</TableCell>
                      <TableCell align="right">{option.value || "-"}</TableCell>
                      <TableCell align="right">{subtotal !== "-" ? `${Number(subtotal).toFixed(2)} €` : "-"}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell colSpan={3} sx={{ fontWeight: 'bold', textAlign: 'right' }}>Total:</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 'bold' }}>{getTotalPrice()} €</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Charge</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TextField
                label="Charge Amount"
                type="number"
                value={currentPoolAction.charge || 0}
                onChange={(e) => handleChargeChange(e.target.value)}
                InputProps={{
                  endAdornment: <InputAdornment position="end">€</InputAdornment>,
                }}
                size="small"
                sx={{ width: 150 }}
              />
              <Typography variant="body2" color="text.secondary">
                Amount to be charged to the owner
              </Typography>
            </Box>
          </Box>

          {poolAction.done && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">Completed On</Typography>
              <Typography>{new Date(poolAction.time_done).toLocaleString()}</Typography>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Edit />}
              onClick={() => setActiveView('edit')}
            >
              Edit Pool Options
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Feed />}
              onClick={() => setActiveView('ticket')}
            >
              Report a Problem
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<MapsHomeWork />}
              onClick={() => setActiveView('property')}
            >
              Property Info
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Shortcut />}
              onClick={() => setActiveView('relatedTickets')}
            >
              Related Tickets
            </Button>

            {!poolAction.done && (
              <Button
                variant="contained"
                color="success"
                startIcon={<Done />}
                onClick={handleComplete}
                disabled={isSubmitting}
              >
                Complete Action
              </Button>
            )}
            <Button
              variant="outlined"
              onClick={onClose}
              sx={{ mt: 1 }}
            >
              Close
            </Button>
          </Box>
        </>
      )}

      {/* Edit View */}
      {activeView === 'edit' && (
        <>
          <Typography variant="h5" gutterBottom>Edit Pool Options</Typography>
          
          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Option</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {editedOptions.map((option, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{getOptionTitle(option._id)}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={option.price || ""}
                        onChange={(e) => handlePriceChange(option._id, e.target.value)}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">€</InputAdornment>,
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={option.value || ""}
                        onChange={(e) => handleValueChange(option._id, e.target.value)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow>
                  <TableCell colSpan={2} align="right">
                    <Typography fontWeight="bold">Total:</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight="bold">{getTotalPrice()} €</Typography>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setActiveView('main')}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveChanges}
              disabled={isSubmitting}
            >
              Save Changes
            </Button>
          </Box>
        </>
      )}

      {/* Ticket View */}
      {activeView === 'ticket' && (
        <Box sx={{ width: '100%' }}>
          <Typography variant="h5" gutterBottom>Report a Problem</Typography>
          <AddTicketForm 
            setShowAddTicketForm={() => setActiveView('main')}
            property={{
              value: {
                _id: poolAction.property,
                name: propertyName
              },
              label: propertyName
            }}
          />
        </Box>
      )}

      {/* Property View */}
      {activeView === 'property' && (
        <Box sx={{ width: '100%' }}>
          <Typography variant="h5" gutterBottom>Property Information</Typography>
          <ViewProperty propertyId={poolAction.property} />
        </Box>
      )}

      {/* Related Tickets View */}
      {activeView === 'relatedTickets' && (
        <Box sx={{ width: '100%' }}>
          <Typography variant="h5" gutterBottom>Related Tickets</Typography>
          <TicketTable
            visibleSearch={false}
            searchProperty={[
              {
                value: {
                  _id: poolAction.property,
                },
              },
            ]}
          />
        </Box>
      )}
    </Box>
  );
};

export default ViewPoolActionForm;
